// Точка входа темы — createTheme для light + dark, ThemeContext, useThemeMode hook.
import { createTheme, type Theme } from '@mui/material/styles';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { lightPalette, darkPalette, toMuiPalette } from './colors';
import { toMuiTypography } from './typography';
import { md3Components } from './components';

const THEME_MODE_STORAGE_KEY = 'sled.theme.mode';

/** Доступные режимы темы. */
export type ThemeMode = 'light' | 'dark' | 'system';

/** Эффективный режим (после резолва `system` через prefers-color-scheme). */
export type EffectiveThemeMode = 'light' | 'dark';

/** Значение ThemeContext. */
export interface ThemeContextValue {
  mode: ThemeMode;
  effectiveMode: EffectiveThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** Резолв системного предпочтения через matchMedia. */
function resolveSystemMode(): EffectiveThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/** Сборка MUI Theme по effectiveMode (light/dark). */
export function buildTheme(mode: EffectiveThemeMode): Theme {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  return createTheme({
    cssVariables: true,
    palette: toMuiPalette(palette),
    typography: toMuiTypography(),
    components: md3Components,
  });
}

/** Provider темы — управляет mode и эффективным effectiveMode. */
export function ThemeModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return 'system';
  });

  const [systemMode, setSystemMode] = useState<EffectiveThemeMode>(() => resolveSystemMode());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, next);
    }
  }, []);

  const effectiveMode: EffectiveThemeMode = mode === 'system' ? systemMode : mode;
  const theme = useMemo(() => buildTheme(effectiveMode), [effectiveMode]);

  const value = useMemo(
    () => ({ mode, effectiveMode, setMode }),
    [mode, effectiveMode, setMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

/** Хук для доступа к ThemeContext. */
export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useThemeMode должен вызываться внутри ThemeModeProvider');
  }
  return ctx;
}
